import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@services/api'

// ============ Suppliers ============

export function useSuppliers(search?: string) {
  return useQuery({
    queryKey: ['suppliers', search],
    queryFn: () =>
      apiClient.get('/suppliers', {
        params: { search, pageSize: 100 },
      }),
  })
}

export function useSupplierDetail(id: number) {
  return useQuery({
    queryKey: ['suppliers', id],
    queryFn: () => apiClient.get(`/suppliers/${id}`),
    enabled: !!id,
  })
}

export function useCreateSupplier() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: any) => apiClient.post('/suppliers', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] })
    },
  })
}

export function useUpdateSupplier(id: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: any) => apiClient.put(`/suppliers/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] })
      queryClient.invalidateQueries({ queryKey: ['suppliers', id] })
    },
  })
}

export function useDeleteSupplier() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => apiClient.delete(`/suppliers/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] })
    },
  })
}

// ============ Products ============

export function useProducts(search?: string, productType?: string) {
  return useQuery({
    queryKey: ['products', search, productType],
    queryFn: () =>
      apiClient.get('/products', {
        params: { search, productType, pageSize: 100 },
      }),
  })
}

export function useProductDetail(id: number) {
  return useQuery({
    queryKey: ['products', id],
    queryFn: () => apiClient.get(`/products/${id}`),
    enabled: !!id,
  })
}

export function useProductCategories() {
  return useQuery({
    queryKey: ['products', 'categories'],
    queryFn: () => apiClient.get('/products/categories'),
  })
}

export function useCreateProduct() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: any) => apiClient.post('/products', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
    },
  })
}

export function useUpdateProduct(id: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: any) => apiClient.put(`/products/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
      queryClient.invalidateQueries({ queryKey: ['products', id] })
    },
  })
}

export function useDeleteProduct() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => apiClient.delete(`/products/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
    },
  })
}

// ============ Purchase Orders ============

export function usePurchaseOrders(search?: string, status?: string) {
  return useQuery({
    queryKey: ['purchase-orders', search, status],
    queryFn: () =>
      apiClient.get('/purchase-orders', {
        params: { search, status, pageSize: 50 },
      }),
  })
}

export function usePurchaseOrderDetail(id: number) {
  return useQuery({
    queryKey: ['purchase-orders', id],
    queryFn: () => apiClient.get(`/purchase-orders/${id}`),
    enabled: !!id,
  })
}

export function useCreatePurchaseOrder() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: any) => apiClient.post('/purchase-orders', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] })
    },
  })
}

export function useUpdatePurchaseOrderStatus(id: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (status: string) =>
      apiClient.patch(`/purchase-orders/${id}/status`, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] })
      queryClient.invalidateQueries({ queryKey: ['purchase-orders', id] })
    },
  })
}

export function useDeletePurchaseOrder() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => apiClient.delete(`/purchase-orders/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] })
    },
  })
}

// ============ Stores ============

export function useStores(search?: string) {
  return useQuery({
    queryKey: ['stores', search],
    queryFn: () =>
      apiClient.get<any>('/stores', {
        params: { search, pageSize: 100 },
      }),
  })
}

// ============ Purchase Bills ============

export function usePurchaseBills(search?: string, status?: string) {
  return useQuery({
    queryKey: ['purchase-bills', search, status],
    queryFn: () =>
      apiClient.get<any>('/purchase-bills', {
        params: { search, status, pageSize: 50 },
      }),
  })
}

export function usePurchaseBillDetail(id: number) {
  return useQuery({
    queryKey: ['purchase-bills', id],
    queryFn: () => apiClient.get(`/purchase-bills/${id}`),
    enabled: !!id,
  })
}

export function useCreatePurchaseBill() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: any) => apiClient.post('/purchase-bills', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-bills'] })
      queryClient.invalidateQueries({ queryKey: ['bulk-stock'] })
      queryClient.invalidateQueries({ queryKey: ['finished-goods'] })
    },
  })
}

export function useDeletePurchaseBill() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => apiClient.delete(`/purchase-bills/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-bills'] })
    },
  })
}
